import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { CalendarHeaderComponent } from 'src/app/shared/components/calendar-header/calendar-header.component';

@Component({
    selector: 'app-project-details-page-date-filter',
    templateUrl: './date-filter.component.html',
    styleUrls: ['../styles.scss', './date-filter.component.scss'],
    standalone: false
})
export class DateFilterComponent implements OnInit, OnDestroy {
  // Shared Figma-styled calendar header (chevrons + white-chip tooltip).
  public calendarHeader = CalendarHeaderComponent;
  @Input() defaultDateValue: string;
  @Input() projectStartDate: string;

  form = new FormGroup({
    startDate: new FormControl(''),
    endDate: new FormControl(''),
  });

  minDate: Date | null = null;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {
  }

  ngOnInit() {

    // Set minimum date if project start date is provided
    if (this.projectStartDate && moment(this.projectStartDate).isValid()) {
      this.minDate = moment(this.projectStartDate).toDate();
    }

    // First, initialize form from query params synchronously
    const qp = this.route.snapshot.queryParams;
    if (qp.startDate && moment(qp.startDate).isValid()) {
      this.form.controls.startDate.setValue(qp.startDate, { emitEvent: false });
    }
    if (qp.endDate && moment(qp.endDate).isValid()) {
      this.form.controls.endDate.setValue(qp.endDate, { emitEvent: false });
    }

    // Now subscribe to query param changes (for future updates)
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(queryParams => {
        if (queryParams.startDate && moment(queryParams.startDate).isValid()) {
          if (this.form.controls.startDate.value !== queryParams.startDate) {
            this.form.controls.startDate.setValue(queryParams.startDate, { emitEvent: false });
          }
        } else {
          this.form.controls.startDate.setValue(null, { emitEvent: false });
        }

        if (queryParams.endDate && moment(queryParams.endDate).isValid()) {
          if (this.form.controls.endDate.value !== queryParams.endDate) {
            this.form.controls.endDate.setValue(queryParams.endDate, { emitEvent: false });
          }
        } else {
          this.form.controls.endDate.setValue(null, { emitEvent: false });
        }
      });

    // Subscribe to form changes AFTER initial setup
    // Only navigate when user actually changes the date picker
    this.form.controls.endDate.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(date => {
        let startDate = this.form.controls.startDate.value;
        startDate = moment(startDate).format('YYYY-MM-DD');

        let endDate = this.form.controls.endDate.value;
        endDate = moment(endDate).format('YYYY-MM-DD');

        // Only navigate if dates are valid and different from current query params
        const currentQP = this.route.snapshot.queryParams;
        if (moment(startDate).isValid() && moment(endDate).isValid()) {
          if (currentQP.startDate !== startDate || currentQP.endDate !== endDate) {
            this.router.navigate([], {
              queryParams: {
                startDate,
                endDate,
              },
              queryParamsHandling: 'merge',
            });
          }
        }
      });
  }

  removeDateFilter($event) {
    this.router.navigate([], {
      queryParams: {
        startDate: null,
        endDate: null,
      },
      queryParamsHandling: 'merge'
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
